import { DOCUMENT, NgClass } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  model,
  OnInit,
  signal,
  TemplateRef,
  viewChild
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, NavigationEnd, Params, Router, RouterLink, RouterLinkActive } from '@angular/router';
import { FormKey } from '@common/components/my-account/my-account-sidepanel/my-account-sidepanel.model';
import { MyAccountSidepanelService } from '@common/components/my-account/my-account-sidepanel/my-account-sidepanel.service';
import { StorageSettingsModalComponent } from '@common/components/my-account/tabs/backup-destination-tab/storage-settings-modal/storage-settings-modal.component';
import { NAVBAR_BREAKPOINT, UUID_REGEX } from '@common/constants';
import { CustomerProblem, PlanMode, RoutePath, ServiceType, Theme } from '@common/enums';
import { Domain, ProductMode, ServiceStatus } from '@common/models';
import { ActiveRoutePipe, FormatCountPipe } from '@common/pipes';
import { BodyStateService, FailedTaskPollingService, QueryParamsService, StatisticsService, ThemeService } from '@common/services';
import { AbilityService } from '@common/services/auth/ability.service';
import { AuthContextService } from '@common/services/auth/auth-context.service';
import { AuthStateService } from '@common/services/auth/auth-state.service';
import { AuthService } from '@common/services/auth/auth.service';
import { DomainEffectService } from '@common/services/domain/domain-effect.service';
import { getServiceUiInfo, navigationLoadingBar$, shouldShowStorageStateModal, showHelpButton } from '@common/utils';
import {
  NgbCollapseModule,
  NgbDropdown,
  NgbDropdownItem,
  NgbDropdownMenu,
  NgbDropdownToggle,
  NgbTooltip
} from '@ng-bootstrap/ng-bootstrap';
import { I18NextPipe } from 'angular-i18next';
import noop from 'lodash/noop';
import {
  Alert,
  AlertService,
  MbsLabelSize,
  MbsPopupType,
  MbsSize,
  ModalService,
  ModalSettings,
  RadioComponent,
  TextEllipsisAppendDirective,
  TextEllipsisComponent
} from 'mbs-ui-kit';
import { Observable, of } from 'rxjs';
import { filter, map, switchMap, take, tap } from 'rxjs/operators';
import { HeaderItem } from './app-header-item.model';

const components = [RadioComponent, TextEllipsisComponent];
const directives = [
  NgClass,
  NgbDropdown,
  NgbDropdownItem,
  NgbDropdownMenu,
  NgbDropdownToggle,
  NgbCollapseModule,
  NgbTooltip,
  RouterLink,
  RouterLinkActive,
  TextEllipsisAppendDirective
];
const modules = [FormsModule];
const pipes = [ActiveRoutePipe, FormatCountPipe, I18NextPipe];

type AlertLink = {
  queryParams?: Params | null;
  path: string;
  text: string;
};

type AlertContent = {
  link?: AlertLink | null;
  status: CustomerProblem;
  text: string;
};

const REGEX_QUERY = /\?.*$/;
const REGEX_HASH = /#.*$/;
const CD_HACK_SERVICES = [ServiceType.Mail, ServiceType.Drive, ServiceType.Contacts, ServiceType.Calendar, ServiceType.History];
const PAGE_ROUTER_URL = {
  [RoutePath.Dashboard]: ServiceType.All,
  [RoutePath.RetentionPolicy]: ServiceType.All,
  // [getServiceUiInfo(ServiceType.Users).url()]: ServiceType.Users,
  // [getServiceUiInfo(ServiceType.History).url()]: ServiceType.History,
  [getServiceUiInfo(ServiceType.Mail).url()]: ServiceType.Mail,
  [getServiceUiInfo(ServiceType.Drive).url()]: ServiceType.Drive,
  [getServiceUiInfo(ServiceType.Contacts).url()]: ServiceType.Contacts,
  [getServiceUiInfo(ServiceType.Calendar).url()]: ServiceType.Calendar,
  [getServiceUiInfo(ServiceType.TeamDrives).url()]: ServiceType.TeamDrives,
  [getServiceUiInfo(ServiceType.SharePoint).url()]: ServiceType.SharePoint,
  [getServiceUiInfo(ServiceType.Teams).url()]: ServiceType.Teams,
  [getServiceUiInfo(ServiceType.Export).url()]: ServiceType.Export
};
const AVAILABLE_PATHS_WITH_ID = [RoutePath.Mail, RoutePath.Drive, RoutePath.Contacts, RoutePath.Calendar, RoutePath.History];
const AVAILABLE_PATHS = [RoutePath.Dashboard, RoutePath.RetentionPolicy, RoutePath.Sharepoint, RoutePath.Teams, RoutePath.SharedDrives];

@Component({
  imports: [...components, ...directives, ...modules, ...pipes],
  selector: 'app-header',
  templateUrl: './app-header.component.html',
  styleUrls: ['./app-header.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class AppHeaderComponent implements OnInit {
  private readonly ability = inject(AbilityService);
  private readonly alertService = inject(AlertService);
  private readonly authContext = inject(AuthContextService);
  private readonly authService = inject(AuthService);
  private readonly authState = inject(AuthStateService);
  private readonly bodyStateService = inject(BodyStateService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly document = inject(DOCUMENT);
  private readonly domainEffect = inject(DomainEffectService);
  private readonly failedTaskPollingService = inject(FailedTaskPollingService);
  private readonly i18nPipe = inject(I18NextPipe);
  private readonly modalService = inject(ModalService);
  private readonly myAccountSidepanelService = inject(MyAccountSidepanelService);
  private readonly queryParamsService = inject(QueryParamsService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly statisticsService = inject(StatisticsService);
  private readonly themeService = inject(ThemeService);

  private readonly accountDropdown = viewChild.required('accountDropdown', { read: NgbDropdown });
  private readonly alertMessageLinkTRef = viewChild('alertMessageLinkTRef', { read: TemplateRef<unknown> });

  private sm = this.bodyStateService.getCssVariable(MbsSize.sm, NAVBAR_BREAKPOINT);
  private lg = this.bodyStateService.getCssVariable(MbsSize.lg, NAVBAR_BREAKPOINT);
  private xl = this.bodyStateService.getCssVariable(MbsSize.xl, NAVBAR_BREAKPOINT);

  protected readonly MbsLabelSize = MbsLabelSize;
  protected readonly MbsSize = MbsSize;
  protected readonly ProductMode = ProductMode;
  protected readonly RoutePath = RoutePath;
  protected readonly Theme = Theme;

  protected readonly isImmutability = this.ability.isImmutability;
  protected readonly isGoogle = this.ability.isGoogle;
  protected readonly isEmergencySignIn = this.ability.isEmergencySignIn;
  protected readonly isAdmin = this.ability.isAdmin;
  protected readonly isHomeUser = this.ability.isHomeUser;
  protected readonly isDomainUser = this.ability.isDomainUser;
  protected readonly isProviderSignIn = this.ability.isProvider;

  protected readonly authUser = this.authState.authUser;
  protected readonly systemInfo = this.authState.systemInfo;
  protected readonly availableServices = this.authState.availableServices;

  protected readonly prefix = this.authContext.prefix;
  protected readonly productMode = this.authContext.productMode;

  protected readonly userId = signal<string>(null);
  protected readonly items = signal<HeaderItem[]>([]);
  protected readonly isNavbarCollapsed = signal<boolean>(true);
  protected readonly theme = model<Theme>(this.themeService.theme());

  protected readonly failedTask = this.failedTaskPollingService.failedTask;
  protected readonly screenX = this.bodyStateService.windowWidth;

  protected readonly showShortSystemLogo = computed(() => this.screenX() < this.sm);
  protected readonly showButtonText = computed(() => this.screenX() >= this.xl);
  protected readonly helpButtonMargin = computed(() => this.screenX() < this.lg);
  protected readonly currentDomain = toSignal(this.domainEffect.fetchDomain(), { initialValue: null });
  protected readonly domainData = computed<Domain>(() => this.isAdmin() && this.currentDomain());
  protected readonly showHelpButton = computed(() => showHelpButton(this.domainData()));

  protected readonly loadingBar = toSignal(navigationLoadingBar$(this.router), { requireSync: true });

  private readonly syncThemeEffect = effect(() => this.theme() !== this.themeService.theme() && this.themeService.toggle());

  get getRouterUrl(): string {
    return this.router.url;
  }

  ngOnInit(): void {
    this.failedTaskPollingService.startPolling();
    this.setUserId();
    this.initHeaderItemLinks(this.availableServices());
    this.initData();
    this.initSteams();
  }

  initData(): void {
    const isBDestinationTabOpened =
      this.shouldOpenMyAccountSidepanel() && this.queryParamsService.getParam('tab') === FormKey.BackupDestination;

    if (shouldShowStorageStateModal(this.systemInfo(), isBDestinationTabOpened)) {
      this.showStorageStateModal();
    }
  }

  initSteams(): void {
    this.router.events
      .pipe(
        filter((e) => e instanceof NavigationEnd),
        filter((e) => this.accessEventByUrl(e)),
        switchMap((e) => this.getServiceStatus(e.url)),
        tap((services) => services.forEach(this.showAlertStatus.bind(this))),
        tap(() => this.setUserId()),
        tap(() => this.accountDropdown().isOpen() && this.accountDropdown().close()),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();

    if (this.accessEventByUrl(this.router)) {
      this.getServiceStatus(this.router.url)
        .pipe(
          tap((services) => services.forEach(this.showAlertStatus.bind(this))),
          takeUntilDestroyed(this.destroyRef)
        )
        .subscribe();
    }

    if (!this.isProviderSignIn() && this.shouldOpenMyAccountSidepanel()) {
      this.myAccountSidepanelService.toggleSidepanel().pipe(takeUntilDestroyed(this.destroyRef)).subscribe();
    }
  }

  private showStorageStateModal(): void {
    const settings: ModalSettings = {
      size: MbsSize.sm
    };

    this.modalService.openCustom<void>(StorageSettingsModalComponent, settings).catch(noop);
  }

  private accessEventByUrl(e: NavigationEnd | { url: string }): boolean {
    const cleanUrl: string = e.url.replace(this.prefix(), '').replace(REGEX_QUERY, '').replace(REGEX_HASH, '');
    const segments: string[] = cleanUrl.split('/').filter(Boolean);

    if (segments.length === 0) return false;

    const hasId = segments.some((segment) => UUID_REGEX.test(segment));

    while (UUID_REGEX.test(segments.at(-1))) {
      segments.pop();
    }

    const pathToCheck = <RoutePath>segments.at(-1);

    if (!pathToCheck) return false;

    if (AVAILABLE_PATHS_WITH_ID.includes(pathToCheck)) {
      return hasId;
    }

    return AVAILABLE_PATHS.includes(pathToCheck);
  }

  private shouldOpenMyAccountSidepanel(): boolean {
    const param = this.queryParamsService.getParam('myAccount');

    return param?.toLowerCase() === 'true';
  }

  private getServiceStatus(url: string): Observable<ServiceStatus[]> {
    return this.pageServiceType(url) == null
      ? of([])
      : this.statisticsService
          .getServiceStatus(this.pageServiceType(this.router.url))
          .pipe(map((services) => services.filter((s) => s.Status != CustomerProblem.NoProblem)));
  }

  private pageServiceType(url: string): ServiceType | string {
    const FIRST_URL_SEGMENT_REGEX = new RegExp('^/[^/]+', 'i');

    try {
      const strippedPrefixUrl = url.replace(this.prefix(), '');
      const routerPathName = FIRST_URL_SEGMENT_REGEX.exec(url)
        ? new URL(strippedPrefixUrl, this.document.baseURI).pathname.split('/').filter(Boolean).shift()
        : '/';

      return PAGE_ROUTER_URL[routerPathName];
    } catch (e) {
      throw new Error('Caught error manually: invalid URL: ', e);
    }
  }

  private setUserId(): void {
    const id: string = this.route?.firstChild?.snapshot?.params?.id || this.authUser().Id;

    this.userId.set(id);
  }

  private initHeaderItemLinks(services: ServiceType[]): void {
    const headerItems = services.map((s) => {
      const serviceUiInfo = getServiceUiInfo(s);
      const needChangeDetectionHack = CD_HACK_SERVICES.includes(s);

      return new HeaderItem(serviceUiInfo.i18nKey, serviceUiInfo.iconCssClass, () => serviceUiInfo.url(), needChangeDetectionHack);
    });

    this.items.set(headerItems);
  }

  private showAlertStatus(status: ServiceStatus): void {
    const type: MbsPopupType = [
      CustomerProblem.AlmostExceededStorageLimit,
      CustomerProblem.ExceededStorageLimit,
      CustomerProblem.PlanWillExpired,
      CustomerProblem.StorageNotSet,
      CustomerProblem.TrialRestriction,
      CustomerProblem.TrialWillExpired
    ].includes(status.Status)
      ? MbsPopupType.warning
      : MbsPopupType.danger;

    const alert = new Alert({
      type,
      content: this.alertMessageLinkTRef(),
      context: this.getAlertContext(status),
      id: status.Status
    });

    this.showOrReplaceAlert(alert);
  }

  private getAlertContext(status: ServiceStatus): AlertContent {
    const { Status, Description } = status || {};
    const link = this.productMode() === ProductMode.Mbs ? this.resolveMbsAlert(Status) : this.resolveStdAlert(Status);

    return { text: Description || '', link, status: Status };
  }

  private resolveMbsAlert(status: CustomerProblem): AlertLink | null {
    if (status === CustomerProblem.StorageImmutabilityActive && !this.router.url.includes(RoutePath.RetentionPolicy)) {
      return {
        path: RoutePath.RetentionPolicy,
        text: this.i18nPipe.transform('header.alert.addPolicy', { format: 'capitalize' })
      };
    }

    return null;
  }

  private resolveStdAlert(status: CustomerProblem): AlertLink | null {
    if (!status) return null;

    switch (status) {
      case CustomerProblem.LicenseProblem:
        return {
          path: RoutePath.Payments,
          queryParams: { plan: PlanMode.Edit },
          text: this.i18nPipe.transform('header.alert.subscribe', { format: 'capitalize' })
        };
      case CustomerProblem.ExceededStorageLimit:
      case CustomerProblem.PlanExpired:
      case CustomerProblem.PlanWillExpired:
      case CustomerProblem.TrialWillExpired:
        return {
          path: RoutePath.Payments,
          queryParams: { plan: PlanMode.Select },
          text: this.i18nPipe.transform('header.alert.subscribe', { format: 'capitalize' })
        };
      case CustomerProblem.StorageNotSet:
        return {
          path: this.router.url.replace(this.prefix(), '').replace(REGEX_QUERY, ''), // current route
          queryParams: { myAccount: true, tab: FormKey.BackupDestination },
          text: this.i18nPipe.transform('header.alert.backupDestination', { format: 'capitalize' })
        };
      case CustomerProblem.InvoiceProblem:
        return {
          path: RoutePath.Payments,
          text: this.i18nPipe.transform('header.alert.checkPaymentDetails', { format: 'capitalize' })
        };
      default:
        throw new Error(`Caught error manually: Unhandled CustomerProblem: ${status}`);
    }
  }

  private showOrReplaceAlert(alert: Alert): void {
    const exists = this.alertService.alerts$.value.some((a) => a.id === alert.id);

    if (exists) {
      this.alertService.clearById(alert.id);
    }

    this.alertService.alert(alert);
  }

  handleOpenMyAccountSidepanel(tab?: FormKey): void {
    if (this.shouldOpenMyAccountSidepanel()) return;

    this.myAccountSidepanelService.toggleSidepanel({ tab }).pipe(take(1)).subscribe();
  }

  handleAlertMessageLinkClick(status: CustomerProblem): void {
    status === CustomerProblem.StorageNotSet ? this.handleOpenMyAccountSidepanel(FormKey.BackupDestination) : undefined;
  }

  handleSignAsAdmin(): void {
    this.authService.signInAsAdmin();
  }

  handleLogout(): void {
    this.authService.logout();
  }

  handleNavbarToggle(): void {
    this.isNavbarCollapsed.update((prev) => !prev);
  }
}
